import React, {
  useContext,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';

import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useWillChange,
  AnimatePresence,
  // useMotionValueEvent,
  // useVelocity,
} from 'framer-motion';

import {
  GradientContext,
  BackgroundContext,
  ZoomLevelContext,
} from '../../containers/BasePage';
import classNames from 'classnames';
import PhotoCred from '../PhotoCred';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useVisibiltyPercentage from '../../hooks/useVisibilityPercentage';
import useWindowWidth from '../../hooks/useWindowWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useScrollDirection } from '../../hooks/useScrollDirection';

import { trackReadMore } from '../../utils/dataLayer';

import s from './VerticalScroller.module.scss';

const SCROLL_DOWN = 0,
  SCROLL_UP = 1;

const VerticalScroller = ({
  titleTop,
  titleBottom,
  items = [],
  backgroundColor,
  anchorLink,
  scrollSpeed,
}) => {
  const { t } = useTranslation();

  const gradient = useContext(GradientContext);
  const background = useContext(BackgroundContext) || {};

  const [ref, isVisible] = useIntersectionObserver(0, undefined, true);

  const { whiteBackground } = background || {};

  const windowWidth = useWindowWidth();
  const desktopMq = useMediaQuery('(min-width: 768px)');

  const [imageWrapHeight, setImageWrapHeight] = useState(0);
  const imageWrapRef = useRef(null);
  const lastCardRef = useRef(null);

  const [windowHeight, setWindowHeight] = useState(1000);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setWindowHeight(window.innerHeight);
  });

  useLayoutEffect(() => {
    const { height: imageWrapHeight } =
      imageWrapRef.current.getBoundingClientRect();
    setImageWrapHeight(imageWrapHeight);
  }, [windowWidth, windowHeight]);

  const [containerHeight, setContainerHeight] = useState(0);
  const cardContainerRef = useRef(null);

  useLayoutEffect(() => {
    const { height: containerHeight } =
      cardContainerRef.current.getBoundingClientRect();
    setContainerHeight(containerHeight);
  }, [windowWidth, windowHeight]);

  const scrollRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: scrollRef,
    offset: ['end end', 'start start'],
  });

  const scrollIndicatorHeight =
    typeof window !== 'undefined'
      ? parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            '--vertScrollerIndicatorTrackHeight'
          )
        )
      : 100;

  const yScrollIndicator = useTransform(
    scrollYProgress,
    [0, 1],
    [`${windowHeight - scrollIndicatorHeight + 1}px`, '0px']
  );

  // const scrollIndicatorScale = useTransform(
  //   useVelocity(scrollYProgress),
  //   [-1, 0, 0, 1],
  //   [1.5, 1, 1, 1.5]
  // );

  const scrollIndicatorWillChange = useWillChange();

  const yScroll = useTransform(
    scrollYProgress,
    [0, 1],
    [
      desktopMq
        ? -(
            containerHeight -
            (lastCardRef.current?.clientHeight || imageWrapHeight * 0.4)
          )
        : -(containerHeight - windowHeight),
      0,
    ]
  );

  const scrollWillChange = useWillChange();

  // DEBUG

  // const [debugY, setDebugY] = useState(0);
  // useMotionValueEvent(scrollYProgress, "change", (latest) => {
  //   setDebugY(latest);
  // })

  // const [debugYScroll, setDebugYScroll] = useState(0);
  // useMotionValueEvent(yScroll, "change", (latest) => {
  //   setDebugYScroll(latest);
  // })

  // const [debugIndicatorScale, setDebugIndicatorScale] = useState(0);
  // useMotionValueEvent(useVelocity(scrollYProgress), "change", (latest) => {
  //   setDebugIndicatorScale(latest);
  // })

  const [currentCardIdx, setCurrentCardIx] = useState(0);
  const [scrollDirection] = useScrollDirection();

  const [cardVisibilities, setCardVisibilities] = useState(
    items.map(() => ({
      active: false,
      percentage: 0,
    }))
  );

  const handleCardVisibilityChanged = (idx, visibility) => {
    setCardVisibilities((prev) => {
      const newVisibilities = [...prev];
      newVisibilities[idx] = {
        idx,
        active: false,
        percentage: parseFloat(visibility.toFixed(1)),
      };

      return newVisibilities;
    });
  };

  useEffect(() => {
    let fullyVisibleCards = cardVisibilities
      .filter((v) => v.percentage === 1)
      .sort((a, b) =>
        scrollDirection === SCROLL_DOWN ? b.idx - a.idx : a.idx - b.idx
      );

    if (fullyVisibleCards.length > 0) {
      setCurrentCardIx(fullyVisibleCards[0].idx);
    }
  }, [cardVisibilities, scrollDirection]);

  const imageInitialAnimProps = {
    opacity: 0,
  };

  const imageEnteredAnimProps = {
    opacity: 1,
    transition: {
      duration: 2,
    },
  };

  const imageExitAnimProps = {
    opacity: 0,
    transition: {
      duration: 0.15,
    },
  };

  const imageWillChange = useWillChange();

  const rootClasses = classNames(s.Root, s[gradient], {
    [s.White]: backgroundColor !== 'black' || whiteBackground,
  });

  const titleClasses = classNames(s.TitleContainer, 'grid', {
    [s.Visible]: isVisible,
  });

  return (
    <>
      {/* DEBUG */}
      {/* <div className={s.Debug}>
        scroll %: {debugY.toFixed(2) * 100}, scroll px: {debugYScroll.toFixed(2)},
        container height: {containerHeight}, no of els: {items.length},
        current el idx: {currentCardIdx + 1},
        raw indicator scale: {debugIndicatorScale.toFixed(2)},
      </div> */}

      <section id={anchorLink} className={rootClasses} ref={ref}>
        <h2 className={s.Title}>
          <span className={titleClasses}>
            <span className={s.TitleTop}>{titleTop}</span>
            <span className={s.TitleBottom}>{titleBottom}</span>
          </span>
        </h2>

        <div
          className={s.Container}
          ref={scrollRef}
          style={{
            height: `${100 * items.length * (1 / scrollSpeed)}vh`,
          }}>
          <div className={s.StickyContainer}>
            <div className={`grid ${s.ScrollIndicatorWrapper}`}>
              <div className={s.ScrollIndicatorContainer}>
                <div className={s.ScrollIndexIndicator}>
                  {currentCardIdx < 9 ? '0' : ''}
                  {currentCardIdx + 1}
                </div>

                <div className={s.ScrollIndicatorTrack} />
                <motion.div
                  className={s.ScrollIndicator}
                  style={{
                    top: yScrollIndicator,
                    willChange: scrollIndicatorWillChange,
                    // scaleY: scrollIndicatorScale,
                  }}
                />
              </div>
            </div>

            <div className={`grid ${s.FixedImageContainer}`}>
              <div className={s.FixedImageWrap} ref={imageWrapRef}>
                <AnimatePresence>
                  {items.map((item, i) => {
                    if (currentCardIdx !== i) {
                      return null;
                    }

                    return (
                      <motion.div
                        key={item.image?.url}
                        initial={imageInitialAnimProps}
                        animate={imageEnteredAnimProps}
                        exit={imageExitAnimProps}
                        style={{ willChange: imageWillChange }}>
                        <Image
                          className={s.FixedImage}
                          src={item.image?.url}
                          alt={item.image?.alt}
                          title={item.image?.title}
                          fill
                        />
                        <PhotoCred credit={item.image?.credit} />
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>

            <motion.div
              ref={cardContainerRef}
              className={s.CardContainer}
              style={{
                y: yScroll,
                willChange: scrollWillChange,
              }}>
              {items.map((item, i) => {
                const isLastItem = i === items.length - 1;

                return (
                  <div key={i} className={s.ItemWrap}>
                    <Card
                      ref={isLastItem ? lastCardRef : null}
                      item={item}
                      i={i}
                      isLastItem={isLastItem}
                      scrollDirection={scrollDirection}
                      wrapperStyle={
                        desktopMq
                          ? {
                              marginTop: `${
                                (windowHeight - imageWrapHeight) / 2 +
                                imageWrapHeight / 2
                              }px`,
                            }
                          : {}
                      }
                      backgroundColor={backgroundColor}
                      onVisibilityChanged={handleCardVisibilityChanged}
                    />
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

VerticalScroller.propTypes = {
  scrollSpeed: PropTypes.number,
};

VerticalScroller.defaultProps = {
  scrollSpeed: 1,
};

const Card = forwardRef((props, wrapperRef) => {
  const {
    item,
    backgroundColor,
    i,
    isLastItem,
    scrollDirection,
    onVisibilityChanged,
    wrapperStyle,
  } = props;

  const [isAnimated, setIsAnimated] = useState();

  const background = useContext(BackgroundContext) || {};
  const browserZoomPercentage = useContext(ZoomLevelContext);
  const { whiteBackground } = background || {};
  const { t } = useTranslation();

  const itemsRef = useRef(null);
  const itemIsVisible = useInView(itemsRef, {
    amount: 0.5,
  });

  const isScrollingDown = scrollDirection === SCROLL_DOWN;

  const [visibilityPercentage] = useVisibiltyPercentage(itemsRef);
  useEffect(() => {
    onVisibilityChanged(i, visibilityPercentage);
  }, [visibilityPercentage]);

  const willChange = useWillChange();

  const itemTitleClasses = classNames(s.ItemTitleContainer, {
    [s.Visible]: itemIsVisible,
  });

  const itemClasses = classNames(s.Item, 'grid', {
    [s.Visible]: itemIsVisible,
  });

  const indexClasses = classNames(s.IndexWrapper, {
    [s.Active]: itemIsVisible,
  });

  const linkClasses = classNames(s.Link, {
    [s.Black]: backgroundColor === 'black' && !whiteBackground,
  });

  const variants = {
    full: {
      opacity: 1,
      y: '0px',
      scale: 1,
    },
    first: {
      opacity: 1,
      y: '0px',
      scale: 1,
    },
    partial: {
      opacity: 0.3,
      scale: 0.99,
      y: '15px',
    },
    initial: {
      opacity: 0.05,
      scale: 0.99,
      y: '15px',
    },
  };

  let zoomedVariants = {};

  if (browserZoomPercentage > 165) {
    zoomedVariants = { ...variants };
    zoomedVariants.partial.opacity = 1;
    zoomedVariants.initial.opacity = 1;
  }

  let initial;
  if (i === 0 && !isAnimated) {
    initial = 'initial';
  } else if (isLastItem && (isAnimated || isScrollingDown)) {
    initial = 'full';
  } else {
    initial = 'initial';
  }

  if (browserZoomPercentage > 165) {
    initial = 'initial';
  }

  const handleTrackReadMore = (e) => {
    e.preventDefault();

    trackReadMore(item?.title, 'Vertical scroller');

    setTimeout(() => {
      window.location.href = item?.href?.href;
    }, 200);
  };

  return (
    <div ref={wrapperRef}>
      <motion.div
        ref={itemsRef}
        key={browserZoomPercentage > 165 ? 'zoomed' : 'not-zoomed'}
        className={itemClasses}
        variants={browserZoomPercentage > 165 ? zoomedVariants : variants}
        initial={initial}
        whileInView={'full'}
        whileNotInView={browserZoomPercentage > 165 ? 'full' : 'partial'}
        transition={{
          duration: 0.5,
          ease: 'easeInOut',
        }}
        viewport={{
          threshold: 0.5,
          amount: 0.5,
          once: false,
          delay: 0.001,
        }}
        transformTemplate={(_, generatedStyles) => generatedStyles}
        onAnimationStart={(definition) => {
          setIsAnimated(true);
        }}
        style={{
          ...wrapperStyle,
          willChange,
        }}>
        <div className={s.Image}>
          <Image
            style={{ objectFit: 'cover' }}
            src={item?.image?.url}
            alt={item?.image?.alt}
            title={item?.image?.title}
            fill
          />
          <PhotoCred credit={item?.image?.credit} />
        </div>

        <div className={s.TextContainer}>
          <div className={indexClasses}>
            <span className={s.Index}>
              {t('VerticalScroller.reason', { index: i + 1 })}
            </span>
          </div>
          <div className={itemTitleClasses}>
            <h3 className={s.ItemTitle}>{item?.title}</h3>
          </div>
          <div className={''}>
            <p className={s.Text}>{item?.text}</p>
          </div>
          {!!item?.href?.href && (
            <Link href={item?.href.href} legacyBehavior>
              <a onClick={handleTrackReadMore} className={linkClasses}>
                <Image
                  width={20}
                  height={20}
                  src={
                    backgroundColor !== 'black'
                      ? '/img/icon-arrow-right-black.svg'
                      : '/img/icon-arrow-right-white.svg'
                  }
                  alt=""
                  className={s.Arrow}
                />
                {t('Generic.readMore')}
                <span className="sr-only">{' ' + item?.title}</span>
              </a>
            </Link>
          )}
        </div>
      </motion.div>
    </div>
  );
});

Card.displayName = 'Card';

export default VerticalScroller;
